import { z } from 'zod';
import { MachineSampleSchema, RunnerMachineOsSchema } from '../runner-agent/shapes';

/** A machine is offline when its last heartbeat is older than this. */
export const MACHINE_OFFLINE_AFTER_SECONDS = 60;

export const RunnerSourceSchema = z.enum(['agent', 'github', 'mock']);

export const MachineSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  os: RunnerMachineOsSchema,
  hostname: z.string().nullable(),
  arch: z.string().nullable(),
  agentVersion: z.string().nullable(),
  lastSeenAt: z.string().datetime().nullable(),
  online: z.boolean(),
  source: RunnerSourceSchema,
  createdAt: z.string().datetime(),
  /** Most recent sample; when `online` is false this is stale data. */
  latest: MachineSampleSchema.nullable(),
});
export type Machine = z.infer<typeof MachineSchema>;

export const RunnerStatusSchema = z.enum(['busy', 'idle', 'offline']);
export type RunnerStatus = z.infer<typeof RunnerStatusSchema>;

export const RunnerSchema = z.object({
  id: z.string().uuid(),
  repo: z.string(),
  name: z.string(),
  labels: z.array(z.string()),
  machineId: z.string().uuid().nullable(),
  machineName: z.string().nullable(),
  status: RunnerStatusSchema,
  /** Status may be out of date: machine heartbeat or GitHub sync is behind. */
  stale: z.boolean(),
  lastSeenAt: z.string().datetime().nullable(),
  currentJob: z
    .object({
      jobName: z.string().nullable(),
      workflowName: z.string().nullable(),
      runId: z.number().int(),
      htmlUrl: z.string().nullable(),
      startedAt: z.string().datetime().nullable(),
    })
    .nullable(),
  /** Measured process-tree usage from the latest sample; null without an agent. */
  proc: z.object({ cpuPct: z.number(), rssBytes: z.number() }).nullable(),
  source: RunnerSourceSchema,
});
export type Runner = z.infer<typeof RunnerSchema>;

export const RunnerJobStatusSchema = z.enum(['queued', 'waiting', 'in_progress', 'completed']);
export type RunnerJobStatus = z.infer<typeof RunnerJobStatusSchema>;

export const QueueReasonSchema = z.enum(['runner', 'approval', 'dependency', 'concurrency']);
export type QueueReason = z.infer<typeof QueueReasonSchema>;

export const JobSchema = z.object({
  id: z.string().uuid(),
  githubJobId: z.number().int(),
  runId: z.number().int(),
  runAttempt: z.number().int(),
  repo: z.string(),
  workflowName: z.string(),
  jobName: z.string(),
  headBranch: z.string().nullable(),
  labels: z.array(z.string()),
  status: RunnerJobStatusSchema,
  queueReason: QueueReasonSchema.nullable(),
  conclusion: z.string().nullable(),
  /** Set only once GitHub assigns the job — never guessed for queued jobs. */
  runnerName: z.string().nullable(),
  machineId: z.string().uuid().nullable(),
  queuedAt: z.string().datetime().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  htmlUrl: z.string(),
  source: RunnerSourceSchema,
});
export type Job = z.infer<typeof JobSchema>;

export const GithubSyncStatusSchema = z.object({
  configured: z.boolean(),
  lastSyncAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  rateLimitedUntil: z.string().datetime().nullable(),
});
export type GithubSyncStatus = z.infer<typeof GithubSyncStatusSchema>;
